import {
  type ComponentType,
  type CSSProperties,
  useLayoutEffect,
  useRef,
} from "react";
import { useImmer } from "use-immer";

import type {
  RuleGraphConditionSubject,
  RuleGraphDto,
  RuleGraphNode,
  RuleGraphNodeKind,
} from "@intelligems/ig-types";
import { IgChip, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { Box, keyframes, Skeleton, Stack, useTheme } from "@mui/material";
import { Theme } from "@mui/material/styles";
import {
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  type NodeTypes,
  Position,
} from "@xyflow/react";
import {
  ArrowUpDown,
  CircleSlash,
  Database,
  Filter,
  GitBranch,
  type LucideIcon,
  PackageCheck,
  Play,
  Scissors,
  ShieldCheck,
} from "lucide-react";

import {
  cartInputPosition,
  cartInputTargetId,
  layoutRuleGraph,
  resolvedRuleGraphEdges,
  RULE_GRAPH_CARD_INDENT,
  RULE_GRAPH_HEADER_GAP,
  RULE_GRAPH_HEADER_HEIGHT,
  RULE_GRAPH_HEADER_WIDTH,
  RULE_GRAPH_NODE_HEIGHT,
  RULE_GRAPH_NODE_WIDTH,
  RULE_GRAPH_RESULT_WIDTH,
  type RuleGraphLayout,
} from "./layout";
import {
  chipConditionOf,
  conditionSubjectOf,
  inputHeaderSubject,
} from "./conditionSubject";
import { RuleGraphCartInput } from "./RuleGraphCartInput";
import { RuleGraphLaneLabel } from "./RuleGraphCanvasChrome";
import { RuleGraphShopperOutput } from "./RuleGraphShopperOutput";
import { RUN_WALK_LAND_MS } from "./run-walk";
import type {
  RuleGraphCartInput as RuleGraphCartInputData,
  RuleGraphNodeOverlay,
  RuleGraphOverlaysByEdgeId,
  RuleGraphOverlaysByNodeId,
} from "./types";
import { edgeOverlayId, RULE_GRAPH_CART_INPUT_NODE_ID } from "./types";

type RuleGraphFlowNodeData = Record<string, unknown> & {
  node: RuleGraphNode;
  /** True for a visible rule-row header, including a branchless input. */
  isLaneHeader: boolean;
  /** "Priority N" caption for laned branch nodes; undefined otherwise. */
  priority: number | undefined;
  /**
   * The branch header's chip, already resolved against the chain: a
   * fallback row whose condition merely repeats the row above it inherits
   * that guard (it can only run once the row above ran) and shows no chip.
   */
  headerCondition: string | undefined;
  /**
   * Which request context the header's chip is about: a branch's own
   * `meta.conditionSubject`, or for an input heading its own row the single
   * subject its branches share. Picks the sentence around the chip.
   */
  headerSubject: RuleGraphConditionSubject;
  isSelected: boolean;
  onSelect: (nodeId: string) => void;
  overlay: RuleGraphNodeOverlay | undefined;
  hasScenarioOverlay: boolean;
  /** Building state: shimmer bars instead of text, no interaction. */
  skeleton: boolean;
  onMeasure?: (id: string, size: { width: number; height: number }) => void;
};

/**
 * The run walk's one-shot settle on the beat under the cursor: a small rise
 * with a slight overshoot, so the card reads as landing rather than fading.
 */
const runLand = keyframes`
  from { opacity: 0.3; transform: translateY(3px) scale(0.985); }
  60% { opacity: 1; transform: translateY(0) scale(1.006); }
  to { opacity: 1; transform: none; }
`;

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

/**
 * Figma "Canvas / Step node" skeleton bar: a 7px gray/07 pill. Lives inside
 * the card's normal (fixed-size) content box, so the real text landing causes
 * zero layout shift.
 */
const SKELETON_BAR_HEIGHT = 7;

const SkeletonBar = ({ width }: { width: number }) => (
  <Skeleton
    aria-hidden
    variant="rounded"
    width={width}
    height={SKELETON_BAR_HEIGHT}
    sx={(theme) => ({
      backgroundColor: theme.igPrimitives.gray[600],
      // Figma rounds the bar ends fully, not to the card's corner radius.
      // A px string, because a bare number is scaled by the theme's shape unit.
      borderRadius: "999px",
      flexShrink: 0,
      [REDUCED_MOTION]: { animation: "none" },
    })}
  />
);

type RuleGraphCartInputNodeData = Record<string, unknown> & {
  cartInput: RuleGraphCartInputData;
  onMeasure?: RuleGraphFlowNodeData["onMeasure"];
  /** The run walk's cursor is on the cart right now. */
  isActive: boolean;
};

export type RuleGraphCardFlowNode = Node<
  RuleGraphFlowNodeData,
  "ruleGraphNode"
>;
export type RuleGraphCartInputFlowNode = Node<
  RuleGraphCartInputNodeData,
  "ruleGraphCartInput"
>;
export type RuleGraphFlowNode =
  | RuleGraphCardFlowNode
  | RuleGraphCartInputFlowNode;
export type RuleGraphFlowEdge = Edge<
  Record<string, unknown>,
  "straight" | "ruleGraphFlow"
>;

interface KindAccent {
  icon: LucideIcon;
  kindLabel: string;
  foreground: (theme: Theme) => string;
  /** Anchors (input/output/terminal) render the compact card: no summary row. */
  compact?: boolean;
}

/**
 * One card for all graph kinds — they differ only in icon, accent and which meta
 * chips render, unlike the WorkflowEditor's per-node config forms.
 */
const KIND_ACCENTS: Record<RuleGraphNodeKind, KindAccent> = {
  input: {
    icon: Play,
    kindLabel: "Input",
    foreground: (theme) => theme.igPrimitives.slate[900],
    compact: true,
  },
  branch: {
    icon: GitBranch,
    kindLabel: "Branch",
    foreground: (theme) => theme.igPrimitives.blue.A300,
  },
  generator: {
    icon: Database,
    kindLabel: "Source",
    foreground: (theme) => theme.igPrimitives.purple[900],
  },
  filter: {
    icon: Filter,
    kindLabel: "Filter",
    foreground: (theme) => theme.igPrimitives.teal[900],
  },
  sort: {
    icon: ArrowUpDown,
    kindLabel: "Sort",
    foreground: (theme) => theme.igPrimitives.indigo[900],
  },
  limit: {
    icon: Scissors,
    kindLabel: "Limit",
    foreground: (theme) => theme.igPrimitives.orange[900],
  },
  exclusions: {
    icon: ShieldCheck,
    kindLabel: "Exclusions",
    foreground: (theme) => theme.igPrimitives.slate[900],
  },
  output: {
    icon: PackageCheck,
    kindLabel: "Output",
    foreground: (theme) => theme.igPrimitives.green[900],
    compact: true,
  },
  "fallback-terminal": {
    icon: CircleSlash,
    kindLabel: "Fallback",
    foreground: (theme) => theme.igPrimitives.amber[900],
    compact: true,
  },
};

/**
 * The uppercase step label the screenshots read by: the KIND says what the
 * step does, so the eyebrow derives from it and never from stored data.
 */
export const KIND_EYEBROWS: Record<RuleGraphNodeKind, string> = {
  input: "CART",
  branch: "WHEN",
  generator: "SHOW",
  filter: "MATCHING",
  sort: "SORTED BY",
  limit: "UP TO",
  exclusions: "EXCLUSIONS",
  output: "WHAT THE SHOPPER SEES",
  "fallback-terminal": "IF NOTHING MATCHES",
};

/**
 * The fallback terminal's copy is the platform's, not the graph generator's:
 * every serving seam stamps the node's `label` from the rule's `onEmpty`
 * setting ("Hide the upsell" / "Show smart recommendations"), so the card
 * renders the stamped label. This constant is only the floor for a graph
 * that somehow arrives label-less — it must never override a stamp, or a
 * smart-recs rule's canvas would claim the slot hides.
 */
export const FALLBACK_TERMINAL_BODY = "Hide the upsell";

export const fallbackTerminalBodyText = (node: RuleGraphNode): string =>
  node.label.trim() === "" || node.label.trim() === "…"
    ? FALLBACK_TERMINAL_BODY
    : node.label;

/**
 * The card's prominent body line, from the typed meta the graph generators
 * emit — `condition` (MATCHING), `sortKey` (SORTED BY), `limit` (UP TO n
 * products) — falling back to the summary sentence. Meta is validated
 * defensively: a malformed value falls through rather than crashing the
 * canvas.
 */
export const nodeBodyText = (node: RuleGraphNode): string | undefined => {
  const meta = node.meta;
  if (node.kind === "filter" && typeof meta?.condition === "string") {
    return meta.condition;
  }
  if (node.kind === "sort" && typeof meta?.sortKey === "string") {
    return meta.sortKey;
  }
  if (
    node.kind === "limit" &&
    typeof meta?.limit === "number" &&
    Number.isFinite(meta.limit)
  ) {
    return `${meta.limit} product${meta.limit === 1 ? "" : "s"}`;
  }
  return undefined;
};

/**
 * Known aggregate meta keys get a chip on the card; everything else (incl.
 * sampleItems) lives in the details panel so cards stay uniform.
 */
const metaChips = (node: RuleGraphNode): string[] => {
  const chips: string[] = [];
  const itemCount = node.meta?.itemCount;
  if (typeof itemCount === "number") {
    chips.push(`${itemCount} item${itemCount === 1 ? "" : "s"}`);
  }
  const intent = node.meta?.intent;
  if (typeof intent === "string") chips.push(intent);
  return chips;
};

type RuleGraphCardState = "skeleton" | "faded" | "default";

const cardState = ({
  skeleton,
  isFaded,
}: {
  skeleton: boolean;
  isFaded: boolean;
}): RuleGraphCardState => {
  if (skeleton) return "skeleton";
  if (isFaded) return "faded";
  return "default";
};

/**
 * The lane dot's tone. Without a scenario overlay (or while the graph
 * rebuilds) no evaluation is meaningful, so every dot is neutral.
 */
const laneDotStateOf = ({
  skeleton,
  hasScenarioOverlay,
  isFaded,
}: {
  skeleton: boolean;
  hasScenarioOverlay: boolean;
  isFaded: boolean;
}): "default" | "pending" | "filled" => {
  if (skeleton || !hasScenarioOverlay) return "default";
  return isFaded ? "pending" : "filled";
};

/**
 * Which header sentence the row speaks. A row past the first rung of a
 * fallback ladder is reached only because the row above came up empty, so it
 * opens with "If nothing matches" — and composes its own condition onto that
 * when it still has one.
 */
const laneLabelTypeOf = ({
  kind,
  priority,
  headerCondition,
}: {
  kind: RuleGraphNodeKind;
  priority: number | undefined;
  headerCondition: string | undefined;
}): "when" | "fallback" | "fallback-when" => {
  if (kind === "fallback-terminal") return "fallback";
  if (priority === undefined || priority <= 1) return "when";
  return headerCondition === undefined ? "fallback" : "fallback-when";
};

const cardBorderColor = (
  theme: Theme,
  { isSelected, isFaded }: { isSelected: boolean; isFaded: boolean }
): string => {
  if (isSelected) return theme.igPrimitives.gray.A100;
  if (isFaded) return theme.igBorders.core.disabled;
  return theme.igBorders.core.default;
};

/** Dimensions shared by the visible card and its React Flow wrapper. */
const ruleGraphFlowNodeSize = ({
  kind,
  isLaneHeader,
  hasResult,
}: {
  kind: RuleGraphNodeKind;
  isLaneHeader: boolean;
  hasResult: boolean;
}): { width: number; height: number } => {
  if (isLaneHeader && kind !== "fallback-terminal") {
    return { width: RULE_GRAPH_HEADER_WIDTH, height: RULE_GRAPH_HEADER_HEIGHT };
  }
  // As above: the terminal row keeps its full header-plus-card footprint.
  if (isLaneHeader && kind === "fallback-terminal") {
    return {
      width: RULE_GRAPH_HEADER_WIDTH,
      height:
        RULE_GRAPH_HEADER_HEIGHT +
        RULE_GRAPH_HEADER_GAP +
        RULE_GRAPH_NODE_HEIGHT,
    };
  }
  if (hasResult) {
    return { width: RULE_GRAPH_RESULT_WIDTH, height: RULE_GRAPH_NODE_HEIGHT };
  }
  return { width: RULE_GRAPH_NODE_WIDTH, height: RULE_GRAPH_NODE_HEIGHT };
};

/** Chip metrics shared by every chip a card can render. */
const cardChipSx = (isFaded: boolean) => ({
  flexShrink: 0,
  height: 24,
  maxWidth: 220,
  opacity: isFaded ? 0.4 : 1,
});

/**
 * Card text clipped to the room it has, with the whole of it in a tooltip when
 * — and only when — it is actually clipped. Cards are a fixed size so a long
 * label or summary loses its tail; measuring on hover keeps the tooltip off
 * every card that already reads in full.
 */
const ClippedText = ({
  text,
  lines,
  variant,
  fontWeight,
  color,
  wrapperStyle,
  fontSize,
  lineHeight,
}: {
  text: string;
  /** 1 clips with an ellipsis; 2 clamps to two lines. */
  lines: 1 | 2;
  variant: "body2" | "caption";
  fontWeight?: number;
  color?: string;
  /** Layout for the tooltip's wrapper span, which becomes the flex child. */
  wrapperStyle?: CSSProperties;
  /** Override the variant's metrics to sit on a neighbor's baseline. */
  fontSize?: number;
  lineHeight?: number;
}) => {
  const textRef = useRef<HTMLElement | null>(null);
  const [isClipped, setIsClipped] = useImmer(false);

  const measure = () => {
    const element = textRef.current;
    if (element === null) return;
    setIsClipped(
      () =>
        element.scrollWidth > element.clientWidth ||
        element.scrollHeight > element.clientHeight
    );
  };

  // Measure up front as well as on hover: a tooltip whose title only arrives
  // after the pointer is already inside has to win a race with MUI's own
  // open decision, and the layout that decides this is settled at paint.
  useLayoutEffect(measure, [text, lines]);

  return (
    <IgTooltip title={isClipped ? text : ""} wrapperStyle={wrapperStyle}>
      <IgTypography
        ref={textRef}
        variant={variant}
        fontWeight={fontWeight}
        onMouseEnter={measure}
        sx={{
          color,
          fontSize,
          // Figma's card body metrics: 12px/1.5 (Body 2/Regular).
          lineHeight: lineHeight ?? 1.5,
          minWidth: 0,
          overflow: "hidden",
          ...(lines === 1
            ? {
                display: "block",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }),
        }}
      >
        {text}
      </IgTypography>
    </IgTooltip>
  );
};

function useNodeMeasurement<T extends HTMLElement>(
  id: string,
  measure: RuleGraphFlowNodeData["onMeasure"]
) {
  const rootRef = useRef<T | null>(null);
  const measureRef = useRef(measure);
  const enabled = measure !== undefined;
  useLayoutEffect(() => {
    measureRef.current = measure;
  }, [measure]);
  useLayoutEffect(() => {
    const element = rootRef.current;
    if (!element || !enabled) return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = element.offsetWidth,
          height = element.offsetHeight;
        if (width > 0 && height > 0)
          measureRef.current?.(id, { width, height });
      });
    });
    observer.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [enabled, id]);
  return rootRef;
}

export const RuleGraphNodeCard = ({
  data,
}: NodeProps<RuleGraphCardFlowNode>) => {
  const theme = useTheme();
  const {
    node,
    priority,
    headerCondition,
    headerSubject,
    isLaneHeader: isHeader,
    isSelected,
    onSelect,
    hasScenarioOverlay,
    skeleton,
  } = data;
  // While the graph is being rebuilt no evaluation is meaningful: skeleton
  // mode forces every tone neutral by dropping the overlay entirely.
  const overlay = skeleton ? undefined : data.overlay;
  const accent = KIND_ACCENTS[node.kind];
  const chips = metaChips(node);
  // The shopper-sees panel hangs on the node the evaluation ended on: the
  // output column carries the products face; the fallback terminal carries
  // the empty face in its own card slot.
  const result =
    node.kind === "output" || (!isHeader && node.kind === "fallback-terminal")
      ? overlay?.result
      : undefined;
  const terminalResult =
    node.kind === "fallback-terminal" ? overlay?.result : undefined;
  const isFaded = overlay?.dimmed === true;
  const isActive = overlay?.active === true;
  // Lane headers render as the Figma rule-row header line, not as step cards;
  // the terminal also carries its "Hide the upsell" card. They and the
  // shopper-sees panel bring their own surface, so the card drops its own
  // background, border, shadow and padding for them.
  const isChromeless = isHeader || result !== undefined;
  const { width: boxWidth, height: boxHeight } = ruleGraphFlowNodeSize({
    kind: node.kind,
    isLaneHeader: isHeader,
    hasResult: result !== undefined,
  });
  const rootRef = useNodeMeasurement<HTMLButtonElement>(
    node.id,
    data.onMeasure
  );
  const state = cardState({ skeleton, isFaded });
  const laneDotState = laneDotStateOf({
    skeleton,
    hasScenarioOverlay,
    isFaded,
  });
  const bodyText = overlay?.bodyText ?? nodeBodyText(node) ?? node.label;
  const textColor = isFaded
    ? theme.igPrimitives.gray.A100
    : theme.palette.text.primary;
  const kickerColor = isFaded
    ? theme.igPrimitives.gray.A100
    : theme.palette.text.placeholder;

  return (
    <Box
      ref={rootRef}
      aria-label={[accent.kindLabel, node.label].join(" ")}
      className="nodrag nopan"
      data-active={isActive ? "true" : undefined}
      data-kind={node.kind}
      data-state={state}
      // The skeleton is scenery, not a control: a plain div, so nothing
      // half-built sits in the tab order or promises a details panel.
      {...(skeleton
        ? { component: "div" as const }
        : {
            "aria-pressed": isSelected,
            component: "button" as const,
            onClick: () => onSelect(node.id),
            type: "button" as const,
          })}
      sx={(cardTheme) => ({
        alignItems: "flex-start",
        appearance: "none",
        backgroundColor: isChromeless
          ? "transparent"
          : cardTheme.igBackgroundColors.baseLightHigh,
        border: isChromeless
          ? 0
          : `1px solid ${cardBorderColor(cardTheme, { isSelected, isFaded })}`,
        borderRadius: 2,
        boxShadow: isChromeless || isFaded ? "none" : cardTheme.igShadows.low,
        boxSizing: "border-box",
        color: textColor,
        cursor: skeleton ? "default" : "pointer",
        display: "flex",
        flexDirection: "column",
        font: "inherit",
        // Figma: 6px between the card's kicker and body.
        gap: isChromeless ? 0 : 0.75,
        height: result || terminalResult ? "auto" : boxHeight,
        minHeight: boxHeight,
        // Figma: card content is pinned to the top — except the shopper-sees
        // panel, which centers on the card row it sits beside and is allowed
        // to overflow that row's height in both directions.
        justifyContent: result ? "center" : "flex-start",
        minWidth: 0,
        // The shopper-sees panel is content-sized and may run past the
        // declared node height; clipping it would slice its explanation.
        overflow: result || terminalResult ? "visible" : "hidden",
        padding: isChromeless ? 0 : "10px 12px",
        textAlign: "left",
        width: boxWidth,
        // The active run-walk beat settles into place; the guard exists
        // because the clock settles instantly under reduced motion, but a
        // beat mid-flight when the preference flips should still hold still.
        animation: isActive
          ? `${runLand} ${RUN_WALK_LAND_MS}ms ease-out`
          : "none",
        [REDUCED_MOTION]: { animation: "none" },
        "&:hover":
          skeleton || isChromeless
            ? undefined
            : {
                borderColor: cardTheme.igPrimitives.gray.A100,
                boxShadow: cardTheme.igShadows.low,
              },
        "&:focus-visible": {
          outline: `2px solid ${cardTheme.igPrimitives.base.A200}`,
          outlineOffset: 2,
        },
      })}
    >
      <Handle
        id="flow-target"
        type="target"
        position={Position.Left}
        isConnectable={false}
        style={{ opacity: 0 }}
      />
      <Handle
        id="fallback-target"
        type="target"
        position={Position.Top}
        isConnectable={false}
        // On header nodes the fallback chain is the left spine: it enters and
        // leaves at the lane dot, not the node's horizontal center.
        style={{ opacity: 0, ...(isHeader ? { left: 5 } : {}) }}
      />
      {result ? (
        <RuleGraphShopperOutput
          empty={result.empty}
          explanation={result.explanation}
          onEmpty={result.onEmpty}
          products={result.products}
          scenarioContext={result.scenarioContext}
        />
      ) : isHeader ? (
        <Stack gap={0} minWidth={0} width="100%">
          <RuleGraphLaneLabel
            color={textColor}
            // A branch under rebuild shows the shimmer bar in the chip's
            // place, so the phrase still has something after it and must not
            // absorb the missing chip — the copy would otherwise flip to
            // "anything" and back as the graph rebuilds.
            hasCondition={
              node.kind === "branch" &&
              (skeleton || headerCondition !== undefined)
            }
            state={laneDotState}
            subject={headerSubject}
            type={laneLabelTypeOf({
              kind: node.kind,
              priority,
              headerCondition,
            })}
          >
            {skeleton && node.kind === "branch" ? (
              // The header keeps its chipped structural phrase (the subject's
              // "When … " lead-in, never the always-matching variant) — the
              // phrase is derived from the kind and subject and survives a
              // rebuild; only the rule's own words shimmer.
              <SkeletonBar width={140} />
            ) : node.kind === "branch" && headerCondition !== undefined ? (
              // The Figma chips layout: the condition value(s) render as
              // chips after the bold header text.
              <IgChip
                color="info"
                label={headerCondition}
                size="small"
                sx={{ height: 24, maxWidth: 220, opacity: isFaded ? 0.4 : 1 }}
              />
            ) : null}
            {overlay?.chip ? (
              <IgChip
                color={overlay.chip.color}
                label={overlay.chip.label}
                size="small"
                sx={cardChipSx(isFaded)}
              />
            ) : null}
          </RuleGraphLaneLabel>
          {node.kind === "fallback-terminal" && terminalResult ? (
            // The scenario fell through to this row: its card slot renders
            // the shopper-sees empty face instead of the plain card.
            <Box
              sx={{
                ml: `${RULE_GRAPH_CARD_INDENT}px`,
                mt: `${RULE_GRAPH_HEADER_GAP}px`,
              }}
            >
              <RuleGraphShopperOutput
                empty={terminalResult.empty}
                explanation={terminalResult.explanation}
                onEmpty={terminalResult.onEmpty}
                products={terminalResult.products}
                scenarioContext={terminalResult.scenarioContext}
              />
            </Box>
          ) : node.kind === "fallback-terminal" ? (
            <Box
              sx={(cardTheme) => ({
                backgroundColor: isFaded
                  ? "transparent"
                  : cardTheme.igBackgroundColors.baseLightHigh,
                border: `1px solid ${
                  isFaded
                    ? cardTheme.igBorders.core.disabled
                    : cardTheme.igBorders.core.default
                }`,
                borderRadius: 2,
                boxShadow: isFaded ? "none" : cardTheme.igShadows.low,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                height: RULE_GRAPH_NODE_HEIGHT,
                justifyContent: "flex-start",
                ml: `${RULE_GRAPH_CARD_INDENT}px`,
                mt: `${RULE_GRAPH_HEADER_GAP}px`,
                overflow: "hidden",
                padding: "10px 12px",
                width: RULE_GRAPH_NODE_WIDTH,
              })}
            >
              {skeleton ? (
                <SkeletonBar width={150} />
              ) : (
                <ClippedText
                  color={textColor}
                  fontWeight={400}
                  lines={2}
                  text={fallbackTerminalBodyText(node)}
                  variant="body2"
                  wrapperStyle={{
                    display: "block",
                    minWidth: 0,
                    overflow: "hidden",
                    width: "100%",
                  }}
                />
              )}
            </Box>
          ) : null}
        </Stack>
      ) : skeleton ? (
        // The building state: the eyebrow slot and two body lines as shimmer
        // bars, inside the same fixed-size card, so the real text lands with
        // zero layout shift.
        <>
          <SkeletonBar width={44} />
          <SkeletonBar width={124} />
          <SkeletonBar width={86} />
        </>
      ) : (
        <>
          <IgTypography
            sx={{
              color: kickerColor,
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            {KIND_EYEBROWS[node.kind]}
          </IgTypography>
          <ClippedText
            color={textColor}
            fontWeight={400}
            lines={2}
            text={bodyText}
            variant="body2"
            wrapperStyle={{
              display: "block",
              minWidth: 0,
              overflow: "hidden",
              width: "100%",
            }}
          />
          {overlay?.chip || chips.length > 0 ? (
            <Stack alignItems="center" direction="row" gap={0.5} minWidth={0}>
              {overlay?.chip ? (
                <IgChip
                  color={overlay.chip.color}
                  label={overlay.chip.label}
                  size="small"
                  sx={cardChipSx(isFaded)}
                />
              ) : null}
              {chips.map((chip) => (
                <IgChip
                  key={chip}
                  color="info"
                  label={chip}
                  size="small"
                  sx={cardChipSx(isFaded)}
                />
              ))}
            </Stack>
          ) : null}
        </>
      )}
      <Handle
        id="flow-source"
        type="source"
        position={Position.Right}
        isConnectable={false}
        style={{ opacity: 0 }}
      />
      <Handle
        id="fallback-source"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ opacity: 0, ...(isHeader ? { left: 5 } : {}) }}
      />
    </Box>
  );
};

/**
 * The run's cart at the head of the rows. Scenery rather than a control: it
 * is the merchant's own pick said back, so there is no details panel behind
 * it and it stays out of the tab order. Its only handle is the flow source
 * the synthetic edge into the first row header leaves from.
 */
export const RuleGraphCartInputNode = ({
  data,
}: NodeProps<RuleGraphCartInputFlowNode>) => {
  const rootRef = useNodeMeasurement<HTMLDivElement>(
    RULE_GRAPH_CART_INPUT_NODE_ID,
    data.onMeasure
  );
  return (
    <Box
      ref={rootRef}
      aria-label="Cart input"
      className="nodrag nopan"
      data-active={data.isActive ? "true" : undefined}
      data-kind="cart-input"
      role="group"
      sx={{
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        minHeight: RULE_GRAPH_NODE_HEIGHT,
        // Content-sized like the shopper-sees panel: centered on the card row
        // it sits beside and allowed to run past that row's height.
        justifyContent: "center",
        overflow: "visible",
        width: RULE_GRAPH_RESULT_WIDTH,
        animation: data.isActive
          ? `${runLand} ${RUN_WALK_LAND_MS}ms ease-out`
          : "none",
        [REDUCED_MOTION]: { animation: "none" },
      }}
    >
      <RuleGraphCartInput cartInput={data.cartInput} />
      <Handle
        id="flow-source"
        type="source"
        position={Position.Right}
        isConnectable={false}
        style={{ opacity: 0 }}
      />
    </Box>
  );
};

export const RULE_GRAPH_NODE_TYPES: NodeTypes = {
  ruleGraphNode: RuleGraphNodeCard as ComponentType<NodeProps>,
  ruleGraphCartInput: RuleGraphCartInputNode as ComponentType<NodeProps>,
};

/**
 * Nodes the Figma rows do not draw: the cart input only renders when it owns
 * a branchless lane header, and the output only appears once a scenario
 * result gives it something to say.
 */
export const hiddenRuleGraphNodeIds = (
  graph: RuleGraphDto,
  overlaysByNodeId?: RuleGraphOverlaysByNodeId,
  showInputHeader = false
): Set<string> => {
  const hidden = new Set<string>();
  for (const node of graph.nodes) {
    if (node.kind === "input" && !showInputHeader) hidden.add(node.id);
    if (
      node.kind === "output" &&
      overlaysByNodeId?.[node.id]?.result === undefined
    ) {
      hidden.add(node.id);
    }
  }
  return hidden;
};

export const getRuleGraphFlowNodes = ({
  graph,
  selectedNodeId,
  onSelectNode,
  overlaysByNodeId,
  skeleton = false,
  layout: suppliedLayout,
  onMeasure,
}: {
  graph: RuleGraphDto;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  layout?: RuleGraphLayout;
  onMeasure?: RuleGraphFlowNodeData["onMeasure"];
  overlaysByNodeId?: RuleGraphOverlaysByNodeId;
  /** Building state: every card renders shimmer bars, nothing is clickable. */
  skeleton?: boolean;
}): RuleGraphFlowNode[] => {
  const layout = suppliedLayout ?? layoutRuleGraph(graph);
  const { positionsByNodeId, priorityByBranchId, mode, leadInNodeIds } = layout;
  const showInputHeader = mode === "branchless" || leadInNodeIds.length > 0;
  const hasScenarioOverlay = !skeleton && overlaysByNodeId !== undefined;
  const hidden =
    mode === "base"
      ? new Set<string>()
      : hiddenRuleGraphNodeIds(
          graph,
          skeleton ? undefined : overlaysByNodeId,
          showInputHeader
        );
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const inputSubject = inputHeaderSubject(graph);
  // The chip a branch header shows. A fallback row runs only after the row
  // above ran and came up empty, so a condition that repeats that row's is
  // already satisfied — printing it again composes "If nothing matches and
  // cart contains Snowboard" under a row that already said so. A condition
  // that differs is the row's own narrowing and stays — and so does one that
  // merely reads the same about a different context ("Standard" the delivery
  // option is not "Standard" the cart product).
  const headerConditionOf = (node: RuleGraphNode): string | undefined => {
    const own = chipConditionOf(node);
    if (own === undefined || (priorityByBranchId.get(node.id) ?? 1) <= 1) {
      return own;
    }
    const parentEdge = graph.edges.find(
      (edge) => edge.kind === "fallback" && edge.to === node.id
    );
    const source = nodesById.get(parentEdge?.from ?? "");
    const parent = source?.branchId ? nodesById.get(source.branchId) : source;
    return parent !== undefined &&
      chipConditionOf(parent) === own &&
      conditionSubjectOf(parent) === conditionSubjectOf(node)
      ? undefined
      : own;
  };

  // The run's cart, at the head of the rows — only while a run is on the
  // canvas (its overlay carries the cart), never while the graph is being
  // rebuilt, and only where there are honest rows to head.
  const cartOverlay = skeleton
    ? undefined
    : overlaysByNodeId?.[RULE_GRAPH_CART_INPUT_NODE_ID];
  const cartPosition =
    cartOverlay?.cartInput === undefined
      ? null
      : cartInputPosition(layout, graph);
  const cartInputNodes: RuleGraphFlowNode[] =
    cartOverlay?.cartInput !== undefined && cartPosition !== null
      ? [
          {
            id: RULE_GRAPH_CART_INPUT_NODE_ID,
            type: "ruleGraphCartInput",
            position: cartPosition,
            width: RULE_GRAPH_RESULT_WIDTH,
            height: RULE_GRAPH_NODE_HEIGHT,
            style: { pointerEvents: "all" },
            data: {
              cartInput: cartOverlay.cartInput,
              onMeasure,
              isActive: cartOverlay.active === true,
            },
            selected: false,
            draggable: false,
          },
        ]
      : [];

  const graphNodes: RuleGraphFlowNode[] = graph.nodes
    .filter((node) => !hidden.has(node.id))
    .map((node) => {
      const overlay = skeleton ? undefined : overlaysByNodeId?.[node.id];
      const hasResult = overlay?.result !== undefined;
      const isLaneHeader =
        mode !== "base" &&
        (node.kind === "branch" ||
          node.kind === "fallback-terminal" ||
          (showInputHeader && node.kind === "input"));
      const size = ruleGraphFlowNodeSize({
        kind: node.kind,
        isLaneHeader,
        hasResult,
      });

      return {
        id: node.id,
        type: "ruleGraphNode",
        position: positionsByNodeId.get(node.id) ?? { x: 0, y: 0 },
        ...size,
        style: { pointerEvents: "all" },
        data: {
          node,
          isLaneHeader,
          priority: priorityByBranchId.get(node.id),
          headerCondition:
            node.kind === "branch" ? headerConditionOf(node) : undefined,
          headerSubject:
            node.kind === "branch"
              ? conditionSubjectOf(node)
              : node.kind === "input"
                ? inputSubject
                : "cart",
          isSelected: node.id === selectedNodeId,
          onSelect: onSelectNode,
          overlay,
          hasScenarioOverlay,
          skeleton,
          onMeasure: overlay?.result ? onMeasure : undefined,
        },
        selected: node.id === selectedNodeId,
        draggable: false,
      };
    });
  return [...cartInputNodes, ...graphNodes];
};

export const getRuleGraphFlowEdges = (
  graph: RuleGraphDto,
  theme: Theme,
  overlaysByEdgeId?: RuleGraphOverlaysByEdgeId,
  overlaysByNodeId?: RuleGraphOverlaysByNodeId,
  suppliedLayout?: RuleGraphLayout
): RuleGraphFlowEdge[] => {
  const hasScenarioOverlay = overlaysByEdgeId !== undefined;
  const layout = suppliedLayout ?? layoutRuleGraph(graph);
  const { mode, laneOrder, leadInNodeIds } = layout;
  // The input heads a row of its own when it is the only header (branchless)
  // or when shared lead-in steps run ahead of the branch rows.
  const headerInputId =
    mode === "branchless" || leadInNodeIds.length > 0
      ? graph.nodes.find((node) => node.kind === "input")?.id
      : undefined;
  const hidden =
    mode === "base"
      ? new Set<string>()
      : hiddenRuleGraphNodeIds(
          graph,
          overlaysByNodeId,
          headerInputId !== undefined
        );
  const kindById = new Map(graph.nodes.map((node) => [node.id, node.kind]));
  const branchIdByNodeId = new Map(
    graph.nodes.map((node) => [node.id, node.branchId])
  );
  const leadIn = new Set(leadInNodeIds);
  /**
   * Where a fallback edge into another row or the terminal joins the left spine.
   *
   * The rows draw their fallback chain down the lane dots, so an edge that
   * starts on a CARD has no honest line to draw — from the card's underside it
   * runs the width of the row to reach the terminal's dot. Every such source
   * is projected onto the lane header that owns it: its own row's header when
   * the step names a row, and otherwise the last row's header, because a step
   * that runs after every row (the platform exclusions tail, a branchless
   * pipeline's last card) falls through from the bottom of the stack.
   *
   * Lane modes only: the base pass draws a raw DAG with no spine to join.
   */
  const spineAnchorId = (fromId: string): string => {
    if (mode === "base") return fromId;
    if (kindById.get(fromId) === "branch" || fromId === headerInputId) {
      return fromId;
    }
    // A lead-in step joins the spine at the input row it runs under; a step
    // after every row falls through from the bottom of the stack.
    if (leadIn.has(fromId)) return headerInputId ?? fromId;
    return (
      branchIdByNodeId.get(fromId) ??
      headerInputId ??
      laneOrder[laneOrder.length - 1] ??
      fromId
    );
  };

  // The synthetic edge from the run's cart into a row header. It exists
  // exactly when the cart node does (its overlay carries the cart), so a
  // canvas with no run draws no dangling arrow. The run decides WHICH row:
  // the overlay seam already names the edge (`__cart-input__-><row>:flow`),
  // and that key is the target; with no such key the cart enters the first
  // row.
  const cartTargetId =
    Object.keys(overlaysByEdgeId ?? {})
      .find((id) => id.startsWith(`${RULE_GRAPH_CART_INPUT_NODE_ID}->`))
      ?.slice(RULE_GRAPH_CART_INPUT_NODE_ID.length + 2)
      .replace(/:flow$/, "") ?? cartInputTargetId(graph, layout);
  const cartEdges: { from: string; to: string; kind: "flow" }[] =
    overlaysByNodeId?.[RULE_GRAPH_CART_INPUT_NODE_ID]?.cartInput !==
      undefined && cartTargetId !== undefined
      ? [
          {
            from: RULE_GRAPH_CART_INPUT_NODE_ID,
            to: cartTargetId,
            kind: "flow",
          },
        ]
      : [];

  return [...cartEdges, ...resolvedRuleGraphEdges(graph)]
    .filter(
      (edge) =>
        // The cart edge is the renderer's own and never hidden.
        edge.from === RULE_GRAPH_CART_INPUT_NODE_ID ||
        // An edge into or out of a hidden node has nothing to attach to.
        (!hidden.has(edge.from) &&
          !hidden.has(edge.to) &&
          // A lane header draws no arrow into its own pipeline — the cards
          // simply sit beneath it, as in the Figma rows.
          !(
            mode !== "base" &&
            edge.kind === "flow" &&
            (kindById.get(edge.from) === "branch" ||
              edge.from === headerInputId)
          ) &&
          // Nor does flow arrive at a branch header by arrow: a row entered
          // from the lead-in's last card sits beneath that row, and the lane
          // dots already say so. Lane modes only — the base pass is a raw DAG.
          !(
            mode !== "base" &&
            edge.kind === "flow" &&
            kindById.get(edge.to) === "branch"
          ))
    )
    .map((edge) => {
      const edgeOverlay = overlaysByEdgeId?.[edgeOverlayId(edge)];
      const dimmed = edgeOverlay?.dimmed ?? false;
      // The run walk's rail beat: the fallback edge under the cursor thickens
      // and takes the highlight color while it "fills".
      const active = edgeOverlay?.active === true;
      const color =
        hasScenarioOverlay && !dimmed
          ? theme.palette.text.interactive.neutral.default
          : theme.igBackgroundColors.interactive.dark.disabled;
      // Keep the runtime edge id, but attach the line to the lane header that
      // owns the step, so the fallback into the next row stays the vertical
      // spine down the lane dots instead of a line across the row.
      const source =
        edge.kind === "fallback" &&
        (kindById.get(edge.to) === "branch" ||
          kindById.get(edge.to) === "fallback-terminal")
          ? spineAnchorId(edge.from)
          : edge.from;

      return {
        id: edgeOverlayId(edge),
        source,
        sourceHandle:
          edge.kind === "fallback" ? "fallback-source" : "flow-source",
        target: edge.to,
        targetHandle:
          edge.kind === "fallback" ? "fallback-target" : "flow-target",
        // Flow edges are the custom curve (`RuleGraphFlowEdge`): a shared
        // tail step — the platform exclusions card, the output — sits
        // vertically centered across the rows, so a row above or below it has
        // a source and a target on different lines. Drawn straight, that edge
        // leaves the card's right handle as a long diagonal across the canvas;
        // stepped, the 28px card gap leaves no room for a corner radius.
        // Fallback edges stay straight: they are the vertical spine down the
        // lane dots, where a router has nothing to route around.
        type: edge.kind === "flow" ? "ruleGraphFlow" : "straight",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color,
          height: 8,
          width: 8,
        },
        style: {
          stroke: color,
          strokeWidth: active ? 2 : 1.5,
          opacity: dimmed ? 0.35 : 1,
          // Reached edges settle their color/weight rather than snapping —
          // the run walk's approximation of the prototype's rail fill.
          transition:
            "stroke 200ms ease, stroke-width 200ms ease, opacity 200ms ease",
        },
      };
    });
};
